import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import {
  assignScreenerToClass,
  getScreenerAssignmentOptions,
  getClasses
} from "../services/TeacherAPIClient";
import {
  getClassAcademicYear,
  getClassGradeLabel
} from "../utils/classDisplay";
import {
  assertConfirmedMutationResponse
} from "../utils/teacherWorkflowRequests";
import "./TeacherAssignScreenerModal.css";

export const getCurrentAcademicYearLabel = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const startYear = month >= 7 ? year : year - 1;

  return `${startYear}/${String(startYear + 1).slice(-2)}`;
};

export const getAcademicYearStart = (academicYear) => {
  const match = String(academicYear || "").match(/(?:19|20)?\d{2}/);
  if (!match) {
    return null;
  }

  const value = Number(match[0]);
  return value < 100 ? 2000 + value : value;
};

export const isSameAcademicYear = (firstYear, secondYear) => {
  const firstStart = getAcademicYearStart(firstYear);
  const secondStart = getAcademicYearStart(secondYear);

  return firstStart !== null && secondStart !== null && firstStart === secondStart;
};

const BASE_SCREENER_OPTIONS = [
  {
    id: "baseline",
    windowId: "baseline",
    name: "Baseline Screener",
    aliases: ["baseline"]
  },
  {
    id: "midpoint",
    windowId: "midpoint",
    name: "Midpoint Screener",
    aliases: ["midpoint", "mid point", "mid-year", "mid year", "midyear"]
  },
  {
    id: "end-of-year",
    windowId: "end-of-year",
    name: "End Of Year Screener",
    aliases: ["end-of-year", "end of year", "end year", "eoy"]
  }
];

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normaliseComparableText = (value) => normaliseText(value).toLowerCase();

const getWindowIdFromScreenerText = (value) => {
  const comparableValue = normaliseComparableText(value).replace(/[-_]+/g, " ");

  if (!comparableValue) {
    return "";
  }

  const matchingScreener = BASE_SCREENER_OPTIONS.find((screener) =>
    [screener.id, screener.windowId, screener.name, ...(screener.aliases || [])].some(
      (alias) => normaliseComparableText(alias).replace(/[-_]+/g, " ") === comparableValue ||
        comparableValue.includes(normaliseComparableText(alias).replace(/[-_]+/g, " "))
    )
  );

  return matchingScreener?.windowId || "";
};

const getClassId = (classItem) => classItem?.id || classItem?._id;

const getStudentCountFromClass = (classItem) => {
  const possibleCount =
    classItem?.studentNumber ??
    classItem?.studentCount ??
    classItem?.studentsCount ??
    classItem?.pupilCount ??
    classItem?.students?.length;

  const numericCount = Number(possibleCount);
  return Number.isFinite(numericCount) ? numericCount : null;
};

const getScreenerId = (screener) =>
  screener?.assessmentId ||
  screener?.assessmentid ||
  screener?.assessment_id ||
  screener?.id ||
  screener?._id;

const getScreenerName = (screener) =>
  screener?.assessmentName ||
  screener?.assessment_name ||
  screener?.name ||
  screener?.title ||
  screener?.label ||
  getScreenerId(screener) ||
  "Screener";

const getMatchingBaseScreener = (screener) => {
  const id = getScreenerId(screener);
  const name = getScreenerName(screener);
  const windowId = getWindowIdFromScreenerText(name) || getWindowIdFromScreenerText(id);

  return BASE_SCREENER_OPTIONS.find((baseScreener) => baseScreener.windowId === windowId) || null;
};

const normaliseScreener = (screener) => {
  const matchingBaseScreener = getMatchingBaseScreener(screener);
  const id = getScreenerId(screener);
  const name = getScreenerName(screener);

  return {
    ...(matchingBaseScreener || {}),
    ...screener,
    id,
    windowId: matchingBaseScreener?.windowId || getWindowIdFromScreenerText(name) || id,
    name,
    description: screener?.description || "Available for class assignment",
    aliases: matchingBaseScreener?.aliases || []
  };
};

const getScreenerSortOrder = (screener) => {
  const index = BASE_SCREENER_OPTIONS.findIndex((baseScreener) => baseScreener.windowId === screener.windowId);
  return index === -1 ? BASE_SCREENER_OPTIONS.length : index;
};

const buildScreenerOptions = (assessmentScreenersResponse) => {
  const assessmentScreeners = Array.isArray(assessmentScreenersResponse?.screeners)
    ? assessmentScreenersResponse.screeners
    : [];
  return assessmentScreeners
    .map(normaliseScreener)
    .filter((screener) => screener.id && normaliseComparableText(screener.name) !== "split comprehension task")
    .sort((firstScreener, secondScreener) => {
      const orderDifference = getScreenerSortOrder(firstScreener) - getScreenerSortOrder(secondScreener);
      return orderDifference || firstScreener.name.localeCompare(secondScreener.name);
    });
};

const normaliseClass = (classItem, countryGrades) => {
  const grade = getClassGradeLabel(classItem, countryGrades);
  const academicYear = getClassAcademicYear(classItem);
  const studentCount = getStudentCountFromClass(classItem);
  const studentCopy =
    studentCount === null
      ? ""
      : `${studentCount} ${studentCount === 1 ? "child" : "children"}`;
  const detailLabel = [grade, academicYear, studentCopy].filter(Boolean).join(", ");

  return {
    ...classItem,
    id: getClassId(classItem),
    name: classItem?.name || classItem?.className || "Unnamed class",
    grade,
    academicYear,
    studentCount,
    detailLabel
  };
};

const normalisePrefillIds = (value) => Array.from(new Set(
  (Array.isArray(value) ? value : String(value || "").split(","))
    .map((id) => normaliseText(id))
    .filter(Boolean)
));

const normalisePrefill = (prefill) => ({
  classIds: normalisePrefillIds(prefill?.classIds || prefill?.class_ids),
  className: normaliseText(prefill?.className || prefill?.class_name),
  screenerName: normaliseText(prefill?.screenerName || prefill?.screener_name),
  targetAcademicYear: normaliseText(
    prefill?.targetAcademicYear || prefill?.target_academic_year
  )
});

const findSingleExactClass = (classes, className) => {
  const target = normaliseComparableText(className);

  if (!target) {
    return null;
  }

  const matches = classes.filter(
    (classItem) => normaliseComparableText(classItem.name) === target
  );

  return matches.length === 1 ? matches[0] : null;
};

const findSingleExactScreener = (screeners, screenerName) => {
  const target = normaliseComparableText(screenerName);
  const targetWindowId = getWindowIdFromScreenerText(screenerName);

  if (!target) {
    return null;
  }

  const matches = screeners.filter(
    (screener) =>
      normaliseComparableText(screener.name) === target ||
      (targetWindowId && screener.windowId === targetWindowId)
  );

  return matches.length === 1 ? matches[0] : null;
};

function TeacherAssignScreenerModal({
  show,
  handleClose,
  schoolid,
  countryGrades = null,
  prefill = null,
  onViewQrCodes = null,
  embedded = false,
  onWorkflowComplete = null,
  onResolveActiveScreeners = null
}) {
  const [stage, setStage] = useState("choose");
  const [classes, setClasses] = useState([]);
  const [classSearchTerm, setClassSearchTerm] = useState("");
  const [isClassPickerExpanded, setIsClassPickerExpanded] = useState(true);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedScreener, setSelectedScreener] = useState(null);
  const [screenerOptions, setScreenerOptions] = useState([]);
  const [handoffScreenerResponses, setHandoffScreenerResponses] = useState({});
  const [completedAssignment, setCompletedAssignment] = useState(null);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isLoadingScreeners, setIsLoadingScreeners] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);
  const trimmedClassSearchTerm = classSearchTerm.trim();
  const currentAcademicYear = getCurrentAcademicYearLabel();
  const canAssign = Boolean(
    selectedClass &&
    selectedScreener?.available === true &&
    selectedScreener?.counts?.assignable > 0 &&
    !isLoadingScreeners &&
    !isSubmitting
  );

  useEffect(() => {
    if (!show) {
      return;
    }

    setStage("choose");
    setClasses([]);
    setClassSearchTerm(prefillContext.className);
    setIsClassPickerExpanded(true);
    setSelectedClass(null);
    setSelectedScreener(null);
    setScreenerOptions([]);
    setHandoffScreenerResponses({});
    setCompletedAssignment(null);
    setIsSubmitting(false);
    setError("");
    setIsLoadingClasses(true);

    const hasHandoffClassIds = prefillContext.classIds.length > 0;
    const classesRequest = hasHandoffClassIds
      ? Promise.allSettled(
          prefillContext.classIds.map((classId) =>
            getScreenerAssignmentOptions(schoolid, classId)
          )
        )
      : getClasses();

    classesRequest
      .then((classesResponse) => {
        let eligibleClasses;

        if (hasHandoffClassIds) {
          const nextHandoffResponses = {};
          eligibleClasses = classesResponse.flatMap((result, index) => {
            if (result.status !== "fulfilled") {
              return [];
            }

            const requestedClassId = prefillContext.classIds[index];
            const resolvedClass = result.value?.class;
            if (!resolvedClass || getClassId(resolvedClass) !== requestedClassId) {
              return [];
            }

            nextHandoffResponses[requestedClassId] = result.value;
            return [normaliseClass(resolvedClass, countryGrades)];
          });
          setHandoffScreenerResponses(nextHandoffResponses);

          if (eligibleClasses.length < 1) {
            setError(
              "The transitioned classes could not be opened for screener assignment. Request class access and try again."
            );
          }
        } else {
          const normalisedClasses = (Array.isArray(classesResponse) ? classesResponse : [])
            .filter((classItem) => classItem?.active !== false && classItem?.snapshot !== true && classItem?.deleted !== true)
            .map((classItem) => normaliseClass(classItem, countryGrades))
            .filter((classItem) => classItem.id)
            .sort((classA, classB) => classA.name.localeCompare(classB.name));
          eligibleClasses = normalisedClasses.filter(
            (classItem) => isSameAcademicYear(
              classItem.academicYear,
              prefillContext.targetAcademicYear || currentAcademicYear
            )
          );
        }

        const exactClass = findSingleExactClass(eligibleClasses, prefillContext.className);
        const preselectedClass = exactClass || (
          hasHandoffClassIds && eligibleClasses.length === 1
            ? eligibleClasses[0]
            : null
        );

        setClasses(eligibleClasses);
        setSelectedClass(preselectedClass);
        setClassSearchTerm(preselectedClass?.name || prefillContext.className);
        setIsClassPickerExpanded(!preselectedClass);
      })
      .catch((classesError) => {
        console.error("Error loading classes for screener assignment:", classesError);
        setClasses([]);
        setError("Classes could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoadingClasses(false);
      });
  }, [show, prefillContext, countryGrades, currentAcademicYear, schoolid]);

  useEffect(() => {
    if (!show || !selectedClass) {
      setScreenerOptions([]);
      setSelectedScreener(null);
      return;
    }

    setIsLoadingScreeners(true);
    setScreenerOptions([]);
    setSelectedScreener(null);
    setError("");

    const screenersRequest = handoffScreenerResponses[selectedClass.id]
      ? Promise.resolve(handoffScreenerResponses[selectedClass.id])
      : getScreenerAssignmentOptions(schoolid, selectedClass.id);

    screenersRequest
      .then((screenersResponse) => {
        const nextScreenerOptions = buildScreenerOptions(screenersResponse);

        if (nextScreenerOptions.length < 1) {
          setError("No active screeners are available for this class grade and country.");
        }

        setScreenerOptions(nextScreenerOptions);
        setSelectedScreener(findSingleExactScreener(nextScreenerOptions, prefillContext.screenerName));
      })
      .catch((screenersError) => {
        console.error("Error loading screeners for class assignment:", screenersError);
        setScreenerOptions([]);
        setSelectedScreener(null);
        setError("Screeners could not be loaded for this class. Please try again.");
      })
      .finally(() => {
        setIsLoadingScreeners(false);
      });
  }, [show, selectedClass, schoolid, prefillContext.screenerName, handoffScreenerResponses]);

  const filteredClasses = useMemo(() => {
    const query = normaliseComparableText(trimmedClassSearchTerm);

    if (!query) {
      return classes;
    }

    return classes.filter((classItem) =>
      [classItem.name, classItem.grade, classItem.academicYear, classItem.detailLabel].some(
        (value) => normaliseComparableText(value).includes(query)
      )
    );
  }, [classes, trimmedClassSearchTerm]);

  const handleClassSearchChange = (event) => {
    const nextSearchTerm = event.target.value;
    setClassSearchTerm(nextSearchTerm);
    setIsClassPickerExpanded(true);
    setError("");

    if (
      selectedClass &&
      normaliseComparableText(nextSearchTerm) !== normaliseComparableText(selectedClass.name)
    ) {
      setSelectedClass(null);
    }
  };

  const handleAssign = async () => {
    if (!canAssign) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      if (!schoolid) {
        throw new Error("School context is missing.");
      }

      const response = await assignScreenerToClass(schoolid, selectedClass.id, selectedScreener.id);
      assertConfirmedMutationResponse(response, ["completed", "no_change"]);

      setCompletedAssignment({
        classId: selectedClass.id,
        className: selectedClass.name,
        screenerName: selectedScreener.name,
        assignedCount: response.assignedCount,
        skippedCount: response.skippedCount,
        status: response.status,
        results: response.results || response.students || []
      });
      setStage("success");
    } catch (assignError) {
      console.error("Error assigning screener:", assignError);
      setError(
        assignError?.response?.data?.error?.message ||
        assignError?.response?.data?.message ||
        "The screener could not be assigned. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForAnotherAssignment = () => {
    setStage("choose");
    setClassSearchTerm("");
    setIsClassPickerExpanded(true);
    setSelectedClass(null);
    setSelectedScreener(null);
    setCompletedAssignment(null);
    setError("");
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: completedAssignment?.classId || completedAssignment?.className || "",
        message: "Screener assignment complete."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderClassResults = () => {
    if (isLoadingClasses) {
      return (
        <div className="assign-screener-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading classes...</span>
        </div>
      );
    }

    if (filteredClasses.length < 1) {
      return (
        <p className="assign-screener-muted">
          No matching classes found in your class list.
        </p>
      );
    }

    return filteredClasses.map((classItem) => {
      const isSelected = selectedClass?.id === classItem.id;

      return (
        <button
          key={classItem.id}
          type="button"
          className={`assign-screener-result${isSelected ? " selected" : ""}`}
          onClick={() => {
            setSelectedClass(classItem);
            setClassSearchTerm(classItem.name);
            setIsClassPickerExpanded(false);
            setError("");
          }}
          aria-label={`Select ${classItem.name}${classItem.detailLabel ? ` - ${classItem.detailLabel}` : ""}`}
          aria-pressed={isSelected}
        >
          <span className="assign-screener-radio" aria-hidden="true" />
          <span className="assign-screener-result-copy">
            <strong>{classItem.name}</strong>
            {classItem.detailLabel && <span>{classItem.detailLabel}</span>}
          </span>
          {isSelected && <span className="assign-screener-selected-label">Selected</span>}
        </button>
      );
    });
  };

  const renderScreenerResults = () => {
    if (!selectedClass) {
      return (
        <p className="assign-screener-muted">
          Choose a class first to load screeners for its grade.
        </p>
      );
    }

    if (isLoadingScreeners) {
      return (
        <div className="assign-screener-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading screeners...</span>
        </div>
      );
    }

    if (screenerOptions.length < 1) {
      return (
        <p className="assign-screener-muted">
          No screeners are available for this class.
        </p>
      );
    }

    return screenerOptions.map((screener) => {
      const isSelected = selectedScreener?.id === screener.id;
      const isUnavailable = screener.available !== true;

      return (
        <button
          key={screener.id}
          type="button"
          className={`assign-screener-option${isSelected ? " selected" : ""}`}
          onClick={() => {
            setSelectedScreener(screener);
            setError("");
          }}
          aria-label={`Select ${screener.name}`}
          aria-pressed={isSelected}
          disabled={isUnavailable}
        >
          <span className="assign-screener-radio" aria-hidden="true" />
          <span className="assign-screener-result-copy">
            <strong>{screener.name}</strong>
            <span>
              {isUnavailable
                ? screener.reason || "Currently unavailable"
                : `${screener.counts?.assignable ?? 0} pupils can be assigned`}
            </span>
          </span>
          {isSelected && <span className="assign-screener-selected-label">Selected</span>}
        </button>
      );
    });
  };

  const renderSelectedSummary = () => {
    if (!selectedClass && !selectedScreener) {
      return null;
    }

    return (
      <div className="assign-screener-summary">
        <strong>Selected assignment</strong>
        <span className="assign-screener-summary-row">
          <span>Class</span>
          <span>{selectedClass?.name || "Choose a class"}</span>
        </span>
        {selectedScreener?.counts && (
          <div className="assign-screener-impact">
            <strong>Assignment preview</strong>
            <span>{selectedScreener.counts.assignable} will be assigned</span>
            <span>{selectedScreener.counts.alreadyAssignedSame} already have this screener</span>
            <span>{selectedScreener.counts.activeOtherScreener} have another active screener</span>
            <span>{selectedScreener.counts.completedSame} already completed this screener</span>
            <span>{selectedScreener.counts.disabled} are disabled</span>
            {selectedScreener.counts.activeOtherScreener > 0 && onResolveActiveScreeners && (
              <Button
                variant="link"
                className="p-0"
                onClick={() => onResolveActiveScreeners({className: selectedClass?.name})}
              >
                Open reset/reassignment workflow
              </Button>
            )}
          </div>
        )}
        <span className="assign-screener-summary-row">
          <span>Screener</span>
          <span>{selectedScreener?.name || "Choose a screener"}</span>
        </span>
      </div>
    );
  };

  const renderChoose = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Assign Screener</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="assign-screener-intro">
          Choose the class and screener to assign.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group className="mb-3" controlId="assignScreenerClassSearch">
          <Form.Label>Which class needs a screener?</Form.Label>
          <Form.Text as="div" className="assign-screener-field-label">
            Search class
          </Form.Text>
          <Form.Control
            type="search"
            value={classSearchTerm}
            onChange={handleClassSearchChange}
            placeholder="Start typing a class name"
            aria-label="Search class"
          />
        </Form.Group>

        <div className="assign-screener-results" aria-label="Matching classes">
          {selectedClass && !isClassPickerExpanded ? (
            <button
              type="button"
              className="assign-screener-selected-class"
              onClick={() => {
                setSelectedClass(null);
                setClassSearchTerm("");
                setIsClassPickerExpanded(true);
              }}
              aria-label={`Change selected class ${selectedClass.name}`}
            >
              <span className="assign-screener-result-copy">
                <strong>{selectedClass.name}</strong>
                {selectedClass.detailLabel && <span>{selectedClass.detailLabel}</span>}
              </span>
              <span className="assign-screener-selected-label">Change</span>
            </button>
          ) : (
            renderClassResults()
          )}
        </div>

        <Form.Group className="mt-4 mb-3" controlId="assignScreenerSearch">
          <Form.Label>Which screener should be assigned?</Form.Label>
          <Form.Text as="div" className="assign-screener-field-label">
            Choose one of the available screener options.
          </Form.Text>
        </Form.Group>

        <div className="assign-screener-results" aria-label="Matching screeners">
          {renderScreenerResults()}
        </div>

        {renderSelectedSummary()}
      </Modal.Body>
      <Modal.Footer>
        <span className="assign-screener-footer-note">
          {canAssign ? "Ready to assign." : "Choose a class and screener first."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={!canAssign} onClick={handleAssign}>
          Assign screener
        </Button>
      </Modal.Footer>
    </>
  );

  const groupedResults = useMemo(() => {
    return (completedAssignment?.results || []).reduce((groups, result) => {
      groups[result.status] = (groups[result.status] || 0) + 1;
      return groups;
    }, {});
  }, [completedAssignment]);

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">
          {completedAssignment?.status === "no_change" ? "No Changes Made" : "Screener Assigned"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="success" className="assign-screener-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>{completedAssignment?.status === "no_change"
              ? `No pupils in ${completedAssignment?.className} needed a new assignment.`
              : `${completedAssignment?.screenerName} has been assigned to ${completedAssignment?.className}.`}
            </strong>
            {completedAssignment?.status !== "no_change" && (
              <span>Children in this class can scan their QR codes to complete the screener.</span>
            )}
            <span>
              {completedAssignment?.assignedCount ?? 0} assigned, {completedAssignment?.skippedCount ?? 0} skipped.
            </span>
          </span>
        </Alert>
        {Object.keys(groupedResults).length > 0 && (
          <div className="assign-screener-summary">
            <strong>Outcome summary</strong>
            {Object.entries(groupedResults).map(([status, count]) => (
              <span className="assign-screener-summary-row" key={status}>
                <span>{status.replaceAll("_", " ")}</span><span>{count}</span>
              </span>
            ))}
          </div>
        )}
        {(completedAssignment?.results || []).length > 0 && (
          <details className="assign-screener-details">
            <summary>View pupil-level outcomes</summary>
            <ul>
              {completedAssignment.results.map((result) => (
                <li key={result.studentId}>
                  <strong>{result.studentName || result.studentId}</strong>: {result.status.replaceAll("_", " ")}
                  {result.reason ? ` — ${result.reason}` : ""}
                </li>
              ))}
            </ul>
          </details>
        )}
        <div className="assign-screener-next">
          <strong>What happens next</strong>
          <span>{embedded ? "Assign another screener repeats the action." : "View QR codes opens the QR-code workflow for the class."}</span>
        </div>
      </Modal.Body>
      <Modal.Footer className="assign-screener-success-footer">
        <span className="assign-screener-footer-note">
          {completedAssignment?.status === "no_change"
            ? "No assignment records were created."
            : embedded
              ? "Done returns to SENSAI. Assign another screener repeats the action."
              : "Screener assigned. QR codes are the next step."}
        </span>
        <Button variant="outline-secondary" onClick={resetForAnotherAssignment}>
          Assign another screener
        </Button>
        <Button variant={embedded ? "primary" : "outline-secondary"} onClick={handleDone}>
          Done
        </Button>
        {!embedded && onViewQrCodes && (
          <Button
            variant="primary"
            onClick={() => onViewQrCodes()}
          >
            View QR codes
          </Button>
        )}
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-assign-screener-modal"
    >
      {stage === "success" && renderSuccess()}
      {stage === "choose" && renderChoose()}
    </Modal>
  );
}

export default TeacherAssignScreenerModal;
